import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { EmptyState, FormDialog, TableCard } from '~/components';
import { BasicGrid } from '~/components/agGrid/Grid';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { usePeerReferenceRef } from '../account/vaultSelectors';
import { createRefColDefs } from './constants';
import { PeerReqForm } from './PeerReqForm';
import { PendingRequestPrompt } from './PendingRequestPrompt';
import { PeerRequestFormValues } from './types';
const TAGS = CT.peerRefReq;

const PeerRefNoRowsOverlay = () => (
  <EmptyState
    pageKey={PROVIDER_PAGE_KEYS.peerReferences}
    title="No peer references yet"
    cyTag={TAGS.table.emptyState}
  />
);

export const PeerRefReqDetails = () => {
  const { peerReferenceRef } = usePeerReferenceRef();
  if (!peerReferenceRef) {
    return null;
  }

  const { modal, peerRefs, loading, pendingRef } = useSelector(
    peerReferenceRef,
    (state) => state.context
  );
  const pendingExists = useSelector(
    peerReferenceRef,
    (state) => state.matches('$_PENDING_EXISTS') || state.matches('$_RESEND_PENDING')
  );
  const resendingPending = useSelector(peerReferenceRef, (state) =>
    state.matches('$_RESEND_PENDING')
  );

  const resendingRow = useSelector(peerReferenceRef, (state) => state.matches('$_RESEND_ROW'));

  const handleOpen = () => {
    peerReferenceRef.send({ type: 'ON_ADD_REQUEST' });
  };
  const handleClose = () => {
    peerReferenceRef.send({ type: 'ON_CLOSE_MODAL' });
  };
  const handleDownload = (id: number, rowIdx: number) => {
    peerReferenceRef.send({ type: 'ON_GET_REFERENCE', payload: { id } });
  };
  const handleDelete = (id: number) => {
    // MCV-1194: route through the confirmation dialog — the machine opens a
    // dialog whose onConfirm fires ON_DELETE_REFERENCE back into the machine.
    peerReferenceRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } });
  };
  const handleResend = (id: number, email: string) => {
    // [MCV-1838] Opens a confirmation; its onConfirm fires ON_RESEND_REFERENCE.
    peerReferenceRef.send({ type: 'ON_SEND_RESEND_CONFIRMATION', payload: { id, email } });
  };
  const onSubmitSuccess = (values: PeerRequestFormValues) => {
    peerReferenceRef.send({ type: 'ON_SEND_REQUEST', values });
  };
  const onSubmitError = (err: unknown) => {
    peerReferenceRef.send({ type: 'ON_SUBMIT_ERROR', payload: { err } });
  };

  const tableProps = {
    title: 'References',
    addLabel: 'Request a Reference',
    onAddClick: handleOpen,
    layoutTag: TAGS.layout,
    addBtnTag: TAGS.table.addBtn,
  };

  return (
    <>
      <TableCard
        mobilePageHeader
        title={tableProps.title}
        addLabel={tableProps.addLabel}
        onAddClick={tableProps.onAddClick}
        layoutTag={tableProps.layoutTag}
        addBtnTag={tableProps.addBtnTag}
      >
        <BasicGrid
          data={peerRefs}
          loading={loading}
          colDefs={createRefColDefs({
            handleDelete,
            handleDownload,
            handleResend,
            resendDisabled: resendingRow,
          })}
          pagination={true}
          cyTag={TAGS.table.container}
          noRowsOverlayComponent={PeerRefNoRowsOverlay}
        />
      </TableCard>
      <FormDialog
        title={'Peer Reference Request'}
        editing={false}
        creating={modal}
        onClose={handleClose}
      >
        {pendingExists && (
          <PendingRequestPrompt
            email={pendingRef?.email ?? ''}
            resending={resendingPending}
            onResend={() => peerReferenceRef.send({ type: 'ON_RESEND_PENDING' })}
            onCancel={() => peerReferenceRef.send({ type: 'ON_CANCEL_PENDING' })}
          />
        )}
        {/* [MCV-1814] Stays mounted (hidden) while the question is asked, so
            Cancel returns to the form with what was typed. */}
        <div className={pendingExists ? 'hidden' : undefined}>
          <PeerReqForm
            submitSuccess={onSubmitSuccess}
            handleClose={handleClose}
            submitError={onSubmitError}
          />
        </div>
      </FormDialog>
    </>
  );
};
